// src/assets/data/cursos/cripto-blockchain/modulo-1.js
import banner from "../../../courses/module-2/img1.png";

const modulo1 = {
  courseId: "cripto-blockchain",
  id: "modulo-1",
  title: "🧩 MÓDULO 1: ¿Qué es Web3 y por qué importa?",
  estimatedDuration: "1 hora",
  format: "Lectura + actividad de verificación",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivo del módulo",
      content: [
        "Entender qué es la Web3, cómo ha evolucionado Internet desde sus versiones anteriores, y por qué esta nueva etapa digital puede cambiar la forma en que interactuamos con la tecnología, el dinero, las redes sociales y nuestros propios datos.",
      ],
    },
    {
      id: "evolucion",
      navLabel: "Evolución Web",
      title: "🔍 La evolución de Internet: de Web1 a Web3",
      content: [
        {
          type: "table",
          headers: [
            "Versión",
            "¿Qué podías hacer?",
            "¿Quién tenía el poder?",
            "Ejemplos",
          ],
          rows: [
            [
              "Web 1.0 (1990–2004)",
              "Solo leer información",
              "Empresas o universidades",
              "Sitios estáticos, portales de noticias",
            ],
            [
              "Web 2.0 (2004–2020)",
              "Leer, publicar, interactuar, compartir",
              "Grandes plataformas (Google, Meta, TikTok)",
              "YouTube, Instagram, Uber, Amazon",
            ],
            [
              "Web 3.0 (2020–hoy)",
              "Leer, publicar, poseer contenido y datos",
              "Los propios usuarios (gracias al blockchain)",
              "dApps, NFTs, DAOs, metaverso",
            ],
          ],
        },
        {
          type: "paragraph",
          text: "🧠 Resumen: Web1 fue una biblioteca digital. Web2 es una red social dominada por empresas. Web3 es una nueva Internet donde tú tienes el control.",
        },
        {
          type: "paragraph",
          text: "🔎 Nota: También se habla de Web 4.0 y Web 5.0 (IA, casas inteligentes, reconocimiento emocional…), pero hoy nos enfocamos en Web3.",
        },
      ],
    },
    {
      id: "definicion",
      navLabel: "Definición",
      title: "🔍 ¿Qué es Web3?",
      content: [
        {
          type: "paragraph",
          text: "Web3 es una nueva forma de usar Internet basada en tecnología blockchain, donde los usuarios pueden controlar sus datos, su identidad digital y su dinero sin depender de grandes corporaciones o bancos.",
        },
        {
          type: "table",
          headers: ["Término", "¿Qué significa?", "Ejemplo real"],
          rows: [
            [
              "Blockchain",
              "Base de datos pública, segura e inalterable",
              "Cualquiera ve las transacciones pero nadie las modifica",
            ],
            [
              "Descentralización",
              "El poder se reparte; no hay un único dueño",
              "No necesitas permiso ni banco para enviar fondos",
            ],
            [
              "Token",
              "Activo digital con valor en la blockchain",
              "Criptomoneda, NFT, entradas digitales",
            ],
            [
              "Wallet",
              "App que almacena tus criptos e identidad digital",
              "MetaMask, Trust Wallet",
            ],
            [
              "dApp",
              "Aplicación sin servidor central",
              "Juegos Web3, mercados de NFT",
            ],
            [
              "DAO",
              "Organización sin jefe, gobernada por votaciones",
              "Comunidades que deciden juntas su tesorería",
            ],
          ],
        },
        {
          type: "paragraph",
          text: "💡 ¿Por qué importa? Ganas tokens por participar, controlas tu identidad y tu dinero, y aprendes habilidades clave para el futuro.",
        },
      ],
    },
    {
      id: "casos-reales",
      navLabel: "Casos reales",
      title: "🏢 Casos reales del mundo Web3",
      content: [
        {
          type: "table",
          headers: ["Empresa / Proyecto", "¿Cómo utiliza Web3?"],
          rows: [
            [
              "Reddit",
              "Avatares digitales únicos (coleccionables) que los usuarios pueden usar o vender",
            ],
            [
              "Nike y Adidas",
              "Zapatillas digitales con certificado en blockchain (objetos coleccionables)",
            ],
            [
              "Starbucks",
              "Programa de recompensas Web3: ganas objetos digitales especiales",
            ],
            [
              "Brave Browser",
              "Te paga con tokens por ver anuncios voluntariosamente",
            ],
            [
              "Axie Infinity",
              "Videojuego “play to earn” donde ganas criptos jugando",
            ],
          ],
        },
      ],
    },
    {
      id: "videos",
      navLabel: "Videos explicativos",
      title: "📺 Videos explicativos",
      content: [
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=TQGOfo2tWF8",
          text: "🎥 Web3: ¿El Futuro? Qué es y Cómo Funciona (Bybit, 17 min)",
        },
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=-ulNaSStAIw",
          text: "🎥 Bootcamp Web3 para Principiantes (Chainlink Español, 20 min)",
        },
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=86NtlVPhFkc",
          text: "🎥 ¿Qué es Web3? (Cripto en Minutos, 5 min)",
        },
      ],
    },
    {
      id: "lecturas",
      navLabel: "Lecturas",
      title: "📚 Lecturas complementarias",
      content: [
        {
          type: "link",
          href: "https://academy.binance.com/es/articles/what-are-web3-wallets",
          text: "Binance Academy – ¿Qué son las Web3 Wallets?",
        },
        {
          type: "link",
          href: "https://cryptoconexion.com/que-es-la-web-3",
          text: "CryptoConexión – ¿Qué es la Web 3?",
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Actividad",
      title: "✅ Actividad de verificación de conocimiento",
      content: [
        "Responde estas preguntas en tu cuaderno",
        {
          type: "list",
          items: [
            "¿Cuál es la principal diferencia entre Web 2 y Web 3?",
            "¿Qué significa “descentralización” y por qué es importante?",
            "¿Para qué sirve una wallet?",
            "Menciona una empresa que use Web3 y qué aprendiste de su ejemplo.",
            "¿Te gustaría probar una app Web3? ¿Cuál y por qué?",
          ],
        },
      ],
    },
    {
      id: "frase",
      navLabel: "Frase del día",
      title: "🧠 Frase del día",
      content: [
        "“En la Web2 tú eras el producto. En la Web3 tú eres el dueño.”",
      ],
    },
  ],
};

export default modulo1;
